import { getKnownEnvironmentHttpBaseUrl } from "@cmmd-forge/client-runtime";
import { normalizePersistedSavedEnvironmentRecord } from "@cmmd-forge/contracts";
import type {
  EnvironmentId,
  PersistedSavedEnvironmentRecord,
  SavedEnvironmentKind,
} from "@cmmd-forge/contracts";
import { create } from "zustand";

import {
  getSavedEnvironmentRegistry as getPersistedSavedEnvironmentRegistry,
  getSavedEnvironmentSecret as getPersistedSavedEnvironmentSecret,
  removeSavedEnvironmentSecret as removePersistedSavedEnvironmentSecret,
  setSavedEnvironmentRegistry as setPersistedSavedEnvironmentRegistry,
  setSavedEnvironmentSecret as setPersistedSavedEnvironmentSecret,
} from "../../clientPersistenceApi";
import { getPrimaryKnownEnvironment } from "../primary";
import { resolveAttachmentUploadUrl } from "./attachmentUploadUrl";

export interface SavedEnvironmentRecord {
  readonly environmentId: EnvironmentId;
  readonly label: string;
  readonly wsBaseUrl: string;
  readonly httpBaseUrl: string;
  readonly createdAt: string;
  readonly lastConnectedAt: string | null;
  /**
   * Absent on every record written before the Credential Vault could be saved
   * at all, which is why it is optional rather than defaulted: an older record
   * is a project Environment by construction, and guessing would put a kind on
   * rows nothing ever classified.
   */
  readonly kind?: SavedEnvironmentKind;
  readonly cmmdOrganizationId?: string;
  readonly cmmdProjectId?: string;
  readonly runtimeId?: string;
  readonly runtimeTicketExpiresAt?: string;
  readonly lastAuthenticatedPullRequestCapability?: boolean;
}

interface SavedEnvironmentRegistryState {
  readonly byId: Record<EnvironmentId, SavedEnvironmentRecord>;
  /**
   * Mirrors the module-level `savedEnvironmentRegistryHydrated` latch into the
   * store so a component can *subscribe* to it. Until rehydration lands `byId`
   * is empty for everybody, which is indistinguishable from "this user has no
   * connections" — a render that reads the count without also reading this flag
   * shows an onboarding surface to users who are already set up.
   */
  readonly hydrated: boolean;
}

interface SavedEnvironmentRegistryStore extends SavedEnvironmentRegistryState {
  readonly upsert: (record: SavedEnvironmentRecord) => void;
  readonly remove: (environmentId: EnvironmentId) => void;
  readonly markConnected: (environmentId: EnvironmentId, connectedAt: string) => void;
  readonly reset: () => void;
}

let savedEnvironmentRegistryHydrated = false;
let savedEnvironmentRegistryHydrationPromise: Promise<void> | null = null;

function valuesOfSavedEnvironmentRegistry(
  byId: Record<EnvironmentId, SavedEnvironmentRecord>,
): ReadonlyArray<SavedEnvironmentRecord> {
  return Object.values(byId) as ReadonlyArray<SavedEnvironmentRecord>;
}

function toPersistedSavedEnvironmentRecords(
  records: ReadonlyArray<SavedEnvironmentRecord>,
): ReadonlyArray<PersistedSavedEnvironmentRecord> {
  return records.map((record) => normalizePersistedSavedEnvironmentRecord(record));
}

function persistSavedEnvironmentRegistryState(
  byId: Record<EnvironmentId, SavedEnvironmentRecord>,
): void {
  try {
    void setPersistedSavedEnvironmentRegistry(
      toPersistedSavedEnvironmentRecords(valuesOfSavedEnvironmentRegistry(byId)),
    ).catch((error) => {
      console.error("[SAVED_ENVIRONMENTS] persist failed", error);
    });
  } catch (error) {
    console.error("[SAVED_ENVIRONMENTS] persist failed", error);
  }
}
function replaceSavedEnvironmentRegistryState(
  records: ReadonlyArray<SavedEnvironmentRecord>,
): void {
  const currentById = useSavedEnvironmentRegistryStore.getState().byId;
  const hydratedById = Object.fromEntries(records.map((record) => [record.environmentId, record]));
  useSavedEnvironmentRegistryStore.setState({
    byId: {
      ...hydratedById,
      ...currentById,
    },
  });
}

async function hydrateSavedEnvironmentRegistry(): Promise<void> {
  if (savedEnvironmentRegistryHydrated) {
    return;
  }
  if (savedEnvironmentRegistryHydrationPromise) {
    return savedEnvironmentRegistryHydrationPromise;
  }

  const nextHydration = (async () => {
    try {
      const persistedRecords = await getPersistedSavedEnvironmentRegistry();
      replaceSavedEnvironmentRegistryState(persistedRecords);
    } catch (error) {
      console.error("[SAVED_ENVIRONMENTS] hydrate failed", error);
    } finally {
      savedEnvironmentRegistryHydrated = true;
      useSavedEnvironmentRegistryStore.setState({ hydrated: true });
    }
  })();

  const hydrationPromise = nextHydration.finally(() => {
    if (savedEnvironmentRegistryHydrationPromise === hydrationPromise) {
      savedEnvironmentRegistryHydrationPromise = null;
    }
  });
  savedEnvironmentRegistryHydrationPromise = hydrationPromise;

  return savedEnvironmentRegistryHydrationPromise;
}

export const useSavedEnvironmentRegistryStore = create<SavedEnvironmentRegistryStore>()((set) => ({
  byId: {},
  hydrated: savedEnvironmentRegistryHydrated,
  upsert: (record) =>
    set((state) => {
      const byId = {
        ...state.byId,
        [record.environmentId]: record,
      };
      persistSavedEnvironmentRegistryState(byId);
      return { byId };
    }),
  remove: (environmentId) =>
    set((state) => {
      const { [environmentId]: _removed, ...remaining } = state.byId;
      persistSavedEnvironmentRegistryState(remaining);
      return {
        byId: remaining,
      };
    }),
  markConnected: (environmentId, connectedAt) =>
    set((state) => {
      const existing = state.byId[environmentId];
      if (!existing) {
        return state;
      }
      const byId = {
        ...state.byId,
        [environmentId]: {
          ...existing,
          lastConnectedAt: connectedAt,
        },
      };
      persistSavedEnvironmentRegistryState(byId);
      return { byId };
    }),
  reset: () => {
    persistSavedEnvironmentRegistryState({});
    set({
      byId: {},
    });
  },
}));

export function hasSavedEnvironmentRegistryHydrated(): boolean {
  return savedEnvironmentRegistryHydrated;
}

export function waitForSavedEnvironmentRegistryHydration(): Promise<void> {
  if (hasSavedEnvironmentRegistryHydrated()) {
    return Promise.resolve();
  }

  return hydrateSavedEnvironmentRegistry();
}

export function listSavedEnvironmentRecords(): ReadonlyArray<SavedEnvironmentRecord> {
  return Object.values(useSavedEnvironmentRegistryStore.getState().byId).toSorted((left, right) =>
    left.label.localeCompare(right.label),
  );
}

/** Only explicitly authenticated descriptor evidence can make a record eligible. */
export function listPullRequestEligibleSavedEnvironmentRecords(): ReadonlyArray<SavedEnvironmentRecord> {
  return listSavedEnvironmentRecords().filter(
    (record) => record.lastAuthenticatedPullRequestCapability === true,
  );
}

export function getSavedEnvironmentRecord(
  environmentId: EnvironmentId,
): SavedEnvironmentRecord | null {
  return useSavedEnvironmentRegistryStore.getState().byId[environmentId] ?? null;
}

function getEnvironmentHttpBaseUrl(environmentId: EnvironmentId): string | null {
  const primaryEnvironment = getPrimaryKnownEnvironment();
  if (primaryEnvironment?.environmentId === environmentId) {
    return getKnownEnvironmentHttpBaseUrl(primaryEnvironment);
  }

  return getSavedEnvironmentRecord(environmentId)?.httpBaseUrl ?? null;
}

export function resolveEnvironmentHttpUrl(input: {
  readonly environmentId: EnvironmentId;
  readonly pathname: string;
  readonly searchParams?: Record<string, string>;
}): string {
  const httpBaseUrl = getEnvironmentHttpBaseUrl(input.environmentId);
  // Fall back to window.location.origin instead of throwing. Components
  // (ProjectFavicon, message attachment previews) call this synchronously
  // during render. Throwing during render trips the ErrorBoundary and takes
  // down the entire app. The fallback is safe because:
  //   - Production: descriptor is always set, primary lookup wins, fallback never fires.
  //   - Same-origin SPA: window.origin matches the master URL.
  //   - Cross-origin desktop: caller must pre-register the env in the saved registry,
  //     and at worst the URL points at the wrong origin (404, recoverable) instead of
  //     a thrown error (white screen).
  const safeBase = httpBaseUrl ?? (typeof window !== "undefined" ? window.location.origin : null);
  if (!safeBase) {
    throw new Error(`Unable to resolve HTTP base URL for environment ${input.environmentId}.`);
  }

  const url = new URL(safeBase);
  // Preserve any base path carried by the environment's httpBaseUrl. Cloud
  // runtimes are served behind the runtime router at `/r/{routerId}/...`;
  // overwriting `pathname` outright drops that prefix, so the request lands on
  // the router root and 503s (it cannot resolve the target Sprite). Mirrors
  // joinBasePath() in the primary-environment resolver. No-op for the primary /
  // desktop-local envs whose httpBaseUrl has no base path.
  const basePath = url.pathname.endsWith("/") ? url.pathname.slice(0, -1) : url.pathname;
  url.pathname = `${basePath}${input.pathname.startsWith("/") ? input.pathname : `/${input.pathname}`}`;
  if (input.searchParams) {
    url.search = new URLSearchParams(input.searchParams).toString();
  }
  return url.toString();
}

export function ensureEnvironmentAttachmentUploadTarget(environmentId: EnvironmentId): void {
  if (!getEnvironmentHttpBaseUrl(environmentId)) {
    throw new Error(`Unable to resolve HTTP base URL for environment ${environmentId}.`);
  }
}

/**
 * Resolve a signed upload URL only for an environment with known HTTP identity.
 * Mutations must not fall back to the current window origin because that could
 * send bytes to a different environment.
 */
export function resolveEnvironmentAttachmentUploadUrl(input: {
  readonly environmentId: EnvironmentId;
  readonly relativeUrl: string;
}): string {
  ensureEnvironmentAttachmentUploadTarget(input.environmentId);
  return resolveAttachmentUploadUrl({
    httpBaseUrl: getEnvironmentHttpBaseUrl(input.environmentId)!,
    relativeUrl: input.relativeUrl,
  });
}

export function resetSavedEnvironmentRegistryStoreForTests() {
  savedEnvironmentRegistryHydrated = false;
  savedEnvironmentRegistryHydrationPromise = null;
  useSavedEnvironmentRegistryStore.setState({ byId: {} });
}

export async function persistSavedEnvironmentRecord(record: SavedEnvironmentRecord): Promise<void> {
  const byId = {
    ...useSavedEnvironmentRegistryStore.getState().byId,
    [record.environmentId]: record,
  };

  await setPersistedSavedEnvironmentRegistry(
    toPersistedSavedEnvironmentRecords(valuesOfSavedEnvironmentRegistry(byId)),
  );
}

/** Replace one persisted key without leaving a duplicate saved Environment behind. */
export async function replaceSavedEnvironmentRecord(
  previousEnvironmentId: EnvironmentId,
  record: SavedEnvironmentRecord,
): Promise<void> {
  const { [previousEnvironmentId]: _previous, ...remaining } =
    useSavedEnvironmentRegistryStore.getState().byId;
  const byId = { ...remaining, [record.environmentId]: record };
  await setPersistedSavedEnvironmentRegistry(
    toPersistedSavedEnvironmentRecords(valuesOfSavedEnvironmentRegistry(byId)),
  );
  useSavedEnvironmentRegistryStore.setState({ byId });
}

export async function readSavedEnvironmentBearerToken(
  environmentId: EnvironmentId,
): Promise<string | null> {
  return getPersistedSavedEnvironmentSecret(environmentId);
}

export async function writeSavedEnvironmentBearerToken(
  environmentId: EnvironmentId,
  bearerToken: string,
): Promise<boolean> {
  return setPersistedSavedEnvironmentSecret(environmentId, bearerToken);
}

export async function removeSavedEnvironmentBearerToken(
  environmentId: EnvironmentId,
): Promise<void> {
  await removePersistedSavedEnvironmentSecret(environmentId);
}
